import { useState } from 'react';
import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import type { EdoAbonent } from '@expeditor/shared';
import { edoApi } from '../api/integrations';

/**
 * Поле «Код абонента ЭДО» с кнопкой «Найти».
 *
 * Переиспользуется в карточках привлекаемого исполнителя и клиента. По кнопке ищет
 * абонента в ЭДО.МИГ24 по переданному ИНН: единственного подставляет сразу, из
 * нескольких даёт выбрать, пустой ответ/ошибку показывает текстом.
 */
export function EdoSubscriberField({
  value,
  onChange,
  inn,
  label = 'Код абонента ЭДО',
  description = 'Идентификатор в ЭДО.МИГ24 — адрес для электронных документов',
}: {
  value: string;
  onChange: (v: string) => void;
  /** ИНН, по которому искать абонента (пустой — поиск недоступен). */
  inn: string | null | undefined;
  label?: string;
  description?: string;
}) {
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<EdoAbonent[] | null>(null);

  const find = async () => {
    setError(null);
    setResults(null);
    const cleanInn = (inn ?? '').trim();
    if (!cleanInn) {
      setError('Заполните ИНН — по нему ищем абонента в ЭДО.');
      return;
    }
    setSearching(true);
    try {
      const r = await edoApi.findAbonents(cleanInn);
      if (r.abonents.length === 0) {
        setError(
          r.requestStatus === 'NotAllowed'
            ? 'ЭДО отказал в доступе — обратитесь к администратору (настройки интеграции).'
            : 'Абоненты по этому ИНН не найдены.',
        );
      } else if (r.abonents.length === 1) {
        onChange(r.abonents[0].abonentCode);
      } else {
        setResults(r.abonents);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось выполнить поиск в ЭДО.');
    } finally {
      setSearching(false);
    }
  };

  return (
    <Stack gap={4}>
      <Group align="flex-end" gap="xs">
        <TextInput
          label={label}
          description={description}
          placeholder="например, 2AE...-...-..."
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <Button
          variant="default"
          leftSection={<IconSearch size={14} />}
          onClick={find}
          loading={searching}
        >
          Найти
        </Button>
      </Group>
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
      {results && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            Найдено абонентов: {results.length}. Выберите нужного:
          </Text>
          {results.map((a) => (
            <Button
              key={a.abonentCode}
              variant="light"
              justify="flex-start"
              onClick={() => {
                onChange(a.abonentCode);
                setResults(null);
              }}
            >
              <Text size="sm">
                {a.name ?? '—'} · {a.abonentCode}
                {a.operatorName ? ` · ${a.operatorName}` : ''}
              </Text>
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
